import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/stores/auth-store'
import { API_BASE_URL, api } from '@/lib/axios'
import {
  AlertCircle,
  Camera,
  Mail,
  User,
  Calendar,
  Shield,
  Save,
  X,
  Globe,
} from 'lucide-react'
import { LanguageSwitcher } from '@/components/language-switcher'

function LoadingSkeleton() {
  return (
    <div className='grid gap-4 md:grid-cols-3'>
      <Card className='md:col-span-1'>
        <CardContent className='pt-6'>
          <div className='flex flex-col items-center'>
            <Skeleton className='h-32 w-32 rounded-full' />
            <Skeleton className='mt-4 h-6 w-32' />
            <Skeleton className='mt-2 h-4 w-24' />
          </div>
        </CardContent>
      </Card>
      <Card className='md:col-span-2'>
        <CardHeader>
          <Skeleton className='h-6 w-48' />
          <Skeleton className='h-4 w-64' />
        </CardHeader>
        <CardContent className='space-y-4'>
          <Skeleton className='h-10 w-full' />
          <Skeleton className='h-10 w-full' />
          <Skeleton className='h-10 w-full' />
        </CardContent>
      </Card>
    </div>
  )
}

export default function Profile() {
  const { t, i18n } = useTranslation()
  const { user, isLoading, error, getProfile } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  })

  useEffect(() => {
    getProfile()
  }, [getProfile])

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
      })
    }
  }, [user])

  const getAvatarUrl = (avatar: string | null) => {
    if (!avatar) return ''
    if (avatar.startsWith('http')) return avatar
    const baseUrl = API_BASE_URL.replace(/\/api$/, '')
    return `${baseUrl}${avatar}`
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)
    setSaveSuccess(false)

    try {
      const response = await api.patch('/accounts/profile-update/', {
        first_name: formData.first_name,
        last_name: formData.last_name,
      })

      if (response.data.status !== 1) {
        throw new Error(response.data.message || 'Failed to update profile')
      }

      await getProfile()
      setIsEditing(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to update profile'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (user) {
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
      })
    }
    setIsEditing(false)
    setSaveError(null)
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploadingAvatar(true)
    setSaveError(null)

    try {
      const formData = new FormData()
      formData.append('avatar', file)

      const response = await api.patch('/accounts/profile-update/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.status !== 1) {
        throw new Error(response.data.message || 'Failed to upload avatar')
      }

      await getProfile()
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to upload avatar'
      )
    } finally {
      setIsUploadingAvatar(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('profile.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {(error || saveError) && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error || saveError}</span>
        </div>
      )}

      {saveSuccess && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400'>
          <span>{t('profile.updateSuccess')}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {isLoading ? (
          <LoadingSkeleton />
        ) : user ? (
          <div className='grid gap-4 md:grid-cols-3'>
            {/* Avatar Card */}
            <Card className='md:col-span-1'>
              <CardContent className='pt-6'>
                <div className='flex flex-col items-center'>
                  <div className='relative'>
                    <Avatar className='h-32 w-32'>
                      <AvatarImage
                        src={getAvatarUrl(user.avatar)}
                        alt={user.first_name}
                      />
                      <AvatarFallback className='text-3xl'>
                        {user.first_name[0]}
                        {user.last_name[0]}
                      </AvatarFallback>
                    </Avatar>
                    <button
                      onClick={handleAvatarClick}
                      disabled={isUploadingAvatar}
                      className='absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-colors hover:bg-primary/90 disabled:opacity-50'
                    >
                      {isUploadingAvatar ? (
                        <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
                      ) : (
                        <Camera className='h-4 w-4' />
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='image/*'
                      onChange={handleAvatarChange}
                      className='hidden'
                    />
                  </div>
                  <h2 className='mt-4 text-xl font-semibold'>
                    {user.first_name} {user.last_name}
                  </h2>
                  <span className='mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium capitalize text-primary'>
                    <Shield className='h-3 w-3' />
                    {user.role}
                  </span>
                </div>

                <Separator className='my-6' />

                <div className='space-y-4'>
                  <div className='flex items-center gap-3 text-sm'>
                    <Mail className='h-4 w-4 text-muted-foreground' />
                    <span className='text-muted-foreground'>{user.email}</span>
                  </div>
                  <div className='flex items-center gap-3 text-sm'>
                    <Calendar className='h-4 w-4 text-muted-foreground' />
                    <span className='text-muted-foreground'>
                      {t('profile.joined')} {formatDate(user.created_at)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profile Details Card */}
            <Card className='md:col-span-2'>
              <CardHeader className='flex flex-row items-center justify-between'>
                <div>
                  <CardTitle className='flex items-center gap-2'>
                    <User className='h-5 w-5' />
                    {t('profile.personalInfo')}
                  </CardTitle>
                  <CardDescription>{t('profile.manageInfo')}</CardDescription>
                </div>
                {!isEditing ? (
                  <Button variant='outline' onClick={() => setIsEditing(true)}>
                    {t('profile.editProfile')}
                  </Button>
                ) : (
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleCancel}
                      disabled={isSaving}
                    >
                      <X className='mr-2 h-4 w-4' />
                      {t('profile.cancel')}
                    </Button>
                    <Button size='sm' onClick={handleSave} disabled={isSaving}>
                      <Save className='mr-2 h-4 w-4' />
                      {isSaving
                        ? t('profile.saving')
                        : t('profile.saveChanges')}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className='space-y-6'>
                <div className='grid gap-4 md:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='first_name'>{t('profile.firstName')}</Label>
                    {isEditing ? (
                      <Input
                        id='first_name'
                        value={formData.first_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            first_name: e.target.value,
                          })
                        }
                      />
                    ) : (
                      <p className='rounded-md border bg-muted/50 px-3 py-2'>
                        {user.first_name}
                      </p>
                    )}
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='last_name'>{t('profile.lastName')}</Label>
                    {isEditing ? (
                      <Input
                        id='last_name'
                        value={formData.last_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            last_name: e.target.value,
                          })
                        }
                      />
                    ) : (
                      <p className='rounded-md border bg-muted/50 px-3 py-2'>
                        {user.last_name}
                      </p>
                    )}
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='email'>{t('profile.email')}</Label>
                  <p className='rounded-md border bg-muted/50 px-3 py-2 text-muted-foreground'>
                    {user.email}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {t('profile.emailCannotChange')}
                  </p>
                </div>

                <Separator />

                <div className='grid gap-4 md:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label>{t('profile.accountCreated')}</Label>
                    <p className='rounded-md border bg-muted/50 px-3 py-2 text-muted-foreground'>
                      {formatDate(user.created_at)}
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <Label>{t('profile.lastUpdated')}</Label>
                    <p className='rounded-md border bg-muted/50 px-3 py-2 text-muted-foreground'>
                      {formatDate(user.updated_at)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Language Settings Card */}
            <Card className='md:col-span-3'>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Globe className='h-5 w-5' />
                  {t('language.title')}
                </CardTitle>
                <CardDescription>{t('profile.selectLanguage')}</CardDescription>
              </CardHeader>
              <CardContent>
                <LanguageSwitcher />
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className='py-8 text-center text-muted-foreground'>
              {t('profile.unableToLoad')}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}
